<script setup lang="ts">
import {
  HgLayout,
  HgMainNavItem,
  HgSideMenuNavItem,
  HgSupportedLanguage,
  type MenuItem,
} from '@smg-real-estate/domus';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { initSplitStoreFromSession, type SmgSplitSession } from '@smg-real-estate/vue-experiments';
import { computed, onMounted, onServerPrefetch, ref, useSSRContext } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { useAuth0Client } from '@/composables/useAuth0Client';
import { getAuthToken } from '@/helpers/Auth/authClient';
import { getAppMetaInfo } from '@/helpers/SEO/metaInfoHelper';
import { HG_ACCOUNT_BASE_PATH } from '@/router/constants';
import { useGetLanguageSwitchLink, useLocale } from '@/router/routeMeta';
import { SplitFeatureFlags } from '@/splitExperiments/splitFeatureFlags';
import { trackFavouritesNavigation } from '@/tracking/tracking';
import { useHead } from '@unhead/vue';

const { t, tm } = useI18n();
const locale = useLocale();
const route = useRoute();
const store = useStore();
const baseUrl = import.meta.env.VUE_APP_BASE_URL;
const getLanguageSwitchLink = useGetLanguageSwitchLink();
const authToken = ref<string | undefined>();
const auth0Client = useAuth0Client();
const ssrContext = useSSRContext<{ smgSplitSession?: SmgSplitSession }>();

useHead(() => {
  return getAppMetaInfo(locale.value as HgSupportedLanguage);
});

const isNewNavigationEnabled = computed<boolean>(() =>
  store.getters['split/isTreatmentOn'](SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);
const isUserLoggedIn = computed(() => !!authToken.value);

const userAccountMenuItem = computed(() => ({
  label: t('$hg.nav.harmonize.overviewMenuItem.label') as string,
  href: `${HG_ACCOUNT_BASE_PATH}/${locale.value}/overview`,
}));
const mainHeaderMenuItem = computed((): MenuItem => {
  if (isUserLoggedIn.value) {
    return {
      label: tm('$hg.nav.loggedInMenuItem.label') as string,
      children: tm('$hg.nav.privateItems'),
    } as unknown as MenuItem;
  }

  return userAccountMenuItem.value as unknown as MenuItem;
});
const adMenuItem = computed(
  () =>
    tm(
      isNewNavigationEnabled.value
        ? '$hg.nav.topNavNewListingMenuItem'
        : '$hg.nav.harmonize.newListingMenuItem',
    ) as unknown as MenuItem,
);
const favouriteMenuItem = computed(() => tm('$hg.nav.favoriteMenuItem') as unknown as MenuItem);

onMounted(async () => {
  authToken.value = await getAuthToken(auth0Client);
});

onServerPrefetch(() => {
  if (ssrContext?.smgSplitSession) {
    initSplitStoreFromSession(store, ssrContext.smgSplitSession);
  }
});

const handleFavouritesNavigation = (): void => {
  trackFavouritesNavigation(locale.value);
};
</script>

<template>
  <div id="app">
    <HgLayout
      :baseUrl="baseUrl"
      :collapsibleStartUnfolded="false"
      :currentLanguage="locale"
      :currentRoute="route"
      :getLanguageToLink="getLanguageSwitchLink"
      :isTopNavEnabled="isNewNavigationEnabled"
      :isUserLoggedIn="isUserLoggedIn"
    >
      <template #headerMetaAction>
        <HgMainNavItem
          :menuItem="adMenuItem"
          :currentRoute="route"
          :baseUrl="baseUrl"
          :bordered="true"
        />
      </template>
      <template #headerMetaNavigation>
        <FavouritesLink
          :baseUrl="baseUrl"
          :menuItem="favouriteMenuItem"
          :menuNavItem="HgMainNavItem"
          :route="route"
          @click="handleFavouritesNavigation"
        />
        <HgMainNavItem
          :menuItem="mainHeaderMenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          :currentRoute="route"
          dropdownOrientation="right"
        />
      </template>
      <template #sideMenuMetaNavigation>
        <FavouritesLink
          :baseUrl="baseUrl"
          :menuItem="favouriteMenuItem"
          :menuNavItem="HgSideMenuNavItem"
          :route="route"
          @click="handleFavouritesNavigation"
        />
      </template>
      <template #sideMenuAccountNavigation>
        <template v-if="isUserLoggedIn">
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.overviewMenuItem') as unknown as MenuItem"
            icon="PersonOutline"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.settingsMenuItem') as unknown as MenuItem"
            icon="Settings"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.myAdsMenuItem') as unknown as MenuItem"
            icon="Appointment"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.myRealEstateMenuItem') as unknown as MenuItem"
            icon="House"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.searchAlertMenuItem') as unknown as MenuItem"
            icon="Bell"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.tenantPlusMenuItem') as unknown as MenuItem"
            icon="PlusMembership"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
          <HgSideMenuNavItem
            :menuItem="tm('$hg.nav.harmonize.signOutMenuItem') as unknown as MenuItem"
            icon="SignOut"
            :baseUrl="baseUrl"
            :currentRoute="route"
          />
        </template>
      </template>
      <router-view />
    </HgLayout>
  </div>
</template>

<style lang="scss">
@use './assets/globals-hg';

#side-menu-language-switch a {
  &.router-link-exact-active {
    border-bottom: 0;
  }
}
</style>
